import { ref, watch, type Ref } from 'vue'

/**
 * Reactive ref two-way-bound to a `localStorage` key. Reads the
 * initial value at construction; writes through on every mutation
 * (deep watch covers nested object/array changes). Strings round-trip
 * raw; every other type is JSON-encoded.
 *
 *   const sidebarCompact = useLocalStorage<boolean>('sidebar.compact', false)
 *   sidebarCompact.value = true   // persisted
 *   <ToggleSwitch v-model="sidebarCompact" />
 *
 * Why a single composable instead of the previous boilerplate
 * (initialize-from-getItem + watch-to-setItem at every call site):
 * uniform error handling for private-mode / quota / SSR; one place
 * to revisit if we ever switch storage backends (IndexedDB,
 * BroadcastChannel-mirrored state, etc.).
 *
 * Graceful fallback: any failure (`window` undefined during SSR;
 * `localStorage` throwing in private mode or at quota; corrupted
 * value that fails `JSON.parse`) falls back silently to the supplied
 * default. We don't surface storage errors to callers — preference
 * state is best-effort by design.
 *
 * Cross-tab sync is opt-in via `options.listenForChanges`. Off by
 * default because most prefs (theme, sidebar state, view mode) are
 * tab-local and a sync would create surprising remote-driven UI
 * shifts. Turn it on for genuinely shared state (e.g., logout
 * propagation if that ever lands here).
 *
 * Type-coercion note: callers should pass `<T>` matching how their
 * data was previously stored if migrating an existing key. Boolean
 * keys previously stored as `'1'`/`'0'` strings will reset to the
 * default on first read (`JSON.parse('1')` parses to numeric 1, not
 * boolean true — incompatible with `<boolean>`). One-time reset is
 * the accepted migration cost.
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options: { listenForChanges?: boolean } = {},
): Ref<T> {
  const isString = typeof defaultValue === 'string'

  const read = (): T => {
    if (typeof window === 'undefined') return defaultValue
    try {
      const raw = window.localStorage?.getItem(key)
      if (raw === null || raw === undefined) return defaultValue
      return (isString ? raw : JSON.parse(raw)) as T
    } catch {
      return defaultValue
    }
  }

  const state = ref<T>(read()) as Ref<T>

  watch(
    state,
    (v) => {
      if (typeof window === 'undefined') return
      try {
        // `isString` is a runtime narrowing of `defaultValue`'s type
        // that TS can't propagate to the generic `T`. Single cast at
        // the seam where we genuinely know `v` is a `string` because
        // `isString` was true at construction.
        const serialized = isString ? (v as string) : JSON.stringify(v)
        window.localStorage?.setItem(key, serialized)
      } catch {
        /* quota / private mode — silent; preference write is best-effort */
      }
    },
    { deep: true },
  )

  if (options.listenForChanges && typeof window !== 'undefined') {
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== key || e.newValue === null) return
      try {
        state.value = (isString ? e.newValue : JSON.parse(e.newValue)) as T
      } catch {
        /* foreign tab wrote a value we can't parse — ignore */
      }
    })
  }

  return state
}
